import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Delete, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Employee PIN dialog for client check-in — same keypad & silent 4-digit auto-check as staff clock-in. */
export function ClientPinDialog({ open, onClose, onIdentified, identifyPath = "/timesheet/identify", title = "Employee PIN", description = "Enter your kiosk PIN to check clients in or out", testid = "client-pin-dialog" }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("pin"); // "pin" | "phone" (shared PIN → confirm last 4 digits of mobile)
  const [heldPin, setHeldPin] = useState("");
  const latest = useRef("");
  const checking = useRef(false);

  useEffect(() => { if (!open) { setPin(""); latest.current = ""; setBusy(false); setStep("pin"); setHeldPin(""); } }, [open]);

  const identify = async (typed, { silent = false } = {}) => {
    if (busy || (silent && checking.current)) return;
    if (silent) checking.current = true; else setBusy(true);
    const body = step === "phone" ? { pin: heldPin, phone_last4: typed } : { pin: typed };
    try {
      const { data } = await api.post(identifyPath, body);
      if (silent && latest.current !== typed) return;
      onIdentified(data, body.pin, { phone_last4: body.phone_last4 || "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "ambiguous_pin") {
        if (step === "pin") { setHeldPin(typed); setStep("phone"); setPin(""); latest.current = ""; toast.message("This PIN is shared by more than one family", { description: "Enter the last 4 digits of your mobile number to continue." }); }
        else if (!silent) { toast.error(detail.message || "Those digits don't match"); setPin(""); latest.current = ""; }
        return;
      }
      if (!silent) {
        toast.error(formatApiErrorDetail(detail) || "PIN not recognized");
        setPin(""); latest.current = "";
      }
    } finally {
      if (silent) {
        checking.current = false;
        if (latest.current !== typed && latest.current.length >= 4) identify(latest.current, { silent: true });
      } else setBusy(false);
    }
  };

  // iPad Safari: act on the touch itself and swallow the synthetic click that follows so one tap = one key.
  const lastTouch = useRef(0);
  const tap = (fn) => ({
    onPointerDown: (e) => { if (e.pointerType === "touch") { lastTouch.current = Date.now(); fn(); } },
    onClick: () => { if (Date.now() - lastTouch.current > 600) fn(); },
  });

  const press = (d) => {
    if (pin.length >= (step === "phone" ? 4 : 10)) return;
    const next = pin + d;
    setPin(next); latest.current = next;
    if (next.length >= 4) identify(next, { silent: step === "pin" });
  };
  const back = () => setPin((p) => { latest.current = p.slice(0, -1); return latest.current; });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xs rounded-3xl p-6 text-center select-none" data-testid={testid} onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="items-center space-y-0">
          <DialogTitle className="font-display text-lg font-bold text-slate-900">{step === "phone" ? "Confirm it's you" : title}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500" data-testid={step === "phone" ? "client-pin-phone-step" : undefined}>{step === "phone" ? "This PIN is shared — enter the last 4 digits of your mobile number" : description}</DialogDescription>
        </DialogHeader>
        <div className="mb-4 flex justify-center gap-2" data-testid="client-pin-display">
          {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className={`h-3 w-3 rounded-full transition ${i < pin.length ? "bg-slate-900" : "bg-slate-200"}`} />)}
          {pin.length > 6 && <span className="self-center text-xs text-slate-500">+{pin.length - 6}</span>}
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="client-pin-keypad">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
            <button key={d} type="button" {...tap(() => press(d))} disabled={busy} data-testid={`client-pin-key-${d}`}
              className="h-14 rounded-xl bg-slate-100 text-lg font-semibold text-slate-900 hover:bg-slate-200 active:scale-95 disabled:opacity-50 transition">{d}</button>
          ))}
          <button type="button" {...tap(back)} disabled={busy || !pin} data-testid="client-pin-key-back"
            className="h-14 rounded-xl bg-slate-100 text-slate-900 hover:bg-slate-200 disabled:opacity-50 transition inline-flex items-center justify-center"><Delete className="h-5 w-5" /></button>
          <button type="button" {...tap(() => press("0"))} disabled={busy} data-testid="client-pin-key-0"
            className="h-14 rounded-xl bg-slate-100 text-lg font-semibold text-slate-900 hover:bg-slate-200 active:scale-95 disabled:opacity-50 transition">0</button>
          <button type="button" {...tap(() => identify(pin))} disabled={busy || pin.length < 4} data-testid="client-pin-key-enter"
            className="h-14 rounded-xl bg-primary text-lg font-semibold text-white hover:shadow disabled:opacity-50 transition inline-flex items-center justify-center">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Go"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
